/**
 * Initialize Google Tag Manager data layer
 */
const initializeDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
};

/**
 * Extract and persist UTM parameters from URL
 * @returns {Object} UTM parameters object
 */
const extractUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  // Standard UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
      sessionStorage.setItem(key, value);
    } else {
      const storedValue = sessionStorage.getItem(key);
      if (storedValue) {
        utmParams[key] = storedValue;
      }
    }
  });
  
  return utmParams;
};

export const getCurrentUTMParams = () => {
  return extractUTMParameters();
};

/**
 * Load Google Analytics 4 script dynamically
 */
const loadGA4Script = (measurementId) => {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
      resolve();
      return;
    }
    
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Initialize analytics tracking
 */
export const initializeAnalytics = async () => {
  initializeDataLayer();
  extractUTMParameters();
  
  const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  if (measurementId) {
    try {
      await loadGA4Script(measurementId);
      window.gtag('config', measurementId, {
        page_title: document.title,
        page_location: window.location.href,
        ...getCurrentUTMParams()
      });
      console.log('[Analytics] GA4 initialized with measurement ID:', measurementId);
    } catch (error) {
      console.error('[Analytics] Failed to load GA4 script:', error);
    }
  }
  
  console.log('[Analytics] Initialized with UTM params:', getCurrentUTMParams());
};

/**
 * Track custom events to GA4 via GTM
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  try {
    // Get current UTM parameters for attribution
    const utmParams = getCurrentUTMParams();
    
    // Combine event parameters with UTM attribution
    const enhancedParams = {
      ...eventParams,
      ...utmParams,
      timestamp: Date.now(),
      page_location: window.location.href,
      page_title: document.title
    };
    
    // Push to GTM data layer
    if (window.gtag) {
      window.gtag('event', eventName, enhancedParams);
    }
    
    // Also push raw event to data layer for GTM triggers
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...enhancedParams
      });
    }
    
    console.log('[Analytics] Event tracked:', eventName, enhancedParams);
    
  } catch (error) {
    console.warn('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track page views with enhanced data
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 * @param {Object} additionalParams - Additional parameters
 */
export const trackPageView = (pagePath, pageTitle = document.title, additionalParams = {}) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    ...additionalParams
  });
};

/**
 * Track CTA button clicks
 * @param {string} ctaName - CTA identifier
 * @param {string} ctaLocation - Where the CTA was clicked
 * @param {string} destination - Target URL or action
 */
export const trackCTAClick = (ctaName, ctaLocation = 'homepage', destination = '') => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    destination: destination,
    event_category: 'engagement',
    event_label: `${ctaLocation}_${ctaName}`
  });
};

/**
 * Track waitlist form interactions
 * @param {string} action - Form action (submit, success, error)
 * @param {string} formId - Form identifier
 * @param {Object} additionalData - Additional form data
 */
export const trackWaitlistEvent = (action, formId = 'oc_wallet_waitlist', additionalData = {}) => {
  const eventName = action === 'submit' ? 'waitlist_submit' : `waitlist_${action}`;
  
  trackEvent(eventName, {
    form_id: formId,
    form_action: action,
    event_category: 'form_interaction',
    event_label: `${formId}_${action}`,
    ...additionalData
  });
};

/**
 * Add UTM parameters to external URLs
 * @param {string} url - Original URL
 * @param {Object} additionalParams - Additional parameters to add
 * @returns {string} Enhanced URL with UTM parameters
 */
export const addUTMToURL = (url, additionalParams = {}) => {
  try {
    const urlObj = new URL(url);
    const utmParams = getCurrentUTMParams();
    
    // Add current UTM parameters
    Object.entries(utmParams).forEach(([key, value]) => {
      if (value && !urlObj.searchParams.has(key)) {
        urlObj.searchParams.set(key, value);
      }
    });
    
    // Add additional parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) {
        urlObj.searchParams.set(key, value);
      }
    });
    
    return urlObj.toString();
  } catch (error) {
    console.warn('[Analytics] Error adding UTM to URL:', error);
    return url;
  }
};

/**
 * Track external link clicks with UTM propagation
 * @param {string} url - Destination URL
 * @param {string} linkText - Link text or identifier
 * @param {string} location - Where the link was clicked
 */
export const trackExternalLinkClick = (url, linkText = '', location = '') => {
  trackEvent('external_link_click', {
    destination_url: url,
    link_text: linkText,
    click_location: location,
    event_category: 'engagement',
    event_label: `external_${linkText || 'link'}`
  });
};

/**
 * Track errors and exceptions
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {string} location - Where the error occurred
 */
export const trackError = (errorType, errorMessage, location = '') => {
  trackEvent('exception', {
    error_type: errorType,
    error_message: errorMessage,
    error_location: location,
    event_category: 'error',
    fatal: false
  });
};

// Export utility functions
export { 
  initializeDataLayer,
  extractUTMParameters
};
